import { useRouter, type NextRouter } from 'next/router';

import { type LinkField } from '@tmh-website/shared/cms';
import { DefaultLocalePrefix, HomepageSlug } from '@tmh-website/shared/const';
import { convertLinkFieldAttributesArrayToObject } from '@tmh-website/shared/helpers';

type UseParseLinkFieldReturn = (props: { link: LinkField; isStandaloneMode?: boolean }) => {
  href: string;
  attributes: Record<string, string>;
} | null;

export const useParseLinkField = (): UseParseLinkFieldReturn => {
  let router: NextRouter | null = null;

  return ({ link, isStandaloneMode }) => {
    try {
      // This will fail in the Magento environment, so we need to catch the error
      // eslint-disable-next-line react-hooks/rules-of-hooks
      router = useRouter();
    } catch (e) {
      // No router available
    }

    const locale = router?.locale ?? DefaultLocalePrefix;

    const attributes = convertLinkFieldAttributesArrayToObject(link.attributes);

    let href = link.value;
    let anchor = link.anchor && link.anchor?.trim().length > 0 ? link.anchor?.trim() : undefined;

    // Fix links where anchor and value are switched up
    if (link.value && link.value.startsWith('#') && !anchor) {
      anchor = href;
      href = '/';
    }

    // Fix links that are only anchors
    if (!href && anchor) {
      href = '/';
    }

    // Replace HomepageSlug with /
    if (href === `/${HomepageSlug}`) {
      href = '/';
    }

    if (href === null) {
      return null;
    }

    // Add anchor id to link
    if (anchor) {
      if (href === '/' && anchor) {
        // No link value, so we just link to an anchor on the same page
        href = `#${anchor}`;
      } else {
        href += `${href.endsWith('#') ? '' : '#'}${anchor}`;
      }
    }

    // Add locale prefix to href when not using the NextJS router or when it only links to '/'
    if ((isStandaloneMode && locale && href.startsWith('/')) || href === '/') {
      href = `/${locale}${href}`;
    }

    return { href, attributes };
  };
};
